/* ==========================================================================
   GYM STUDIO — 2D MODEL LIBRARY  (birds-eye, extruded)
   ==========================================================================
   Dillon's brief, third revision: the treadmill and the adjustable bench were
   "close to what I had in mind"; the rest were not. What those two had was
   ANATOMY — a belt with a seam, two pads with a hinge between them — so you can
   tell at a glance what you are looking at. Everything here now works that way.

   Two other instructions folded in:

   1. "Give them a more 3D feel while still keeping 2D birds eye." So every solid
      is EXTRUDED: a dark side face offset down-right, a top face over it with a
      lit gradient, and a soft contact shadow beneath the whole machine. It stays
      a true plan view — no perspective, nothing foreshortened, so the footprint
      remains honest and the drawing stays measurable — but it now sits ON the
      floor instead of being painted onto it.

   2. "One model per product type is fine, scaled by product size." So classes
      are per PRODUCT TYPE, not per mechanism: a selectorized leg machine and a
      selectorized chest machine are different drawings, and an 8-stack, a
      5-stack and a 4-stack are three drawings rather than one rectangle with a
      different label.

   Machines are composed from a shared `station()` generator rather than drawn
   one by one. That is deliberate: 30-odd hand-drawn machines drift apart in
   proportion and line weight, while a generator keeps a Chest Press and a Leg
   Press visibly siblings — which is what real equipment lines look like — and
   makes a new type four lines of configuration.

   Each draw(w, d) returns SVG markup in LOCAL coordinates: 0..w across,
   0..d front-to-back, in inches. Requires the <defs> emitted by the plan.

   Fields per class: label · aspect · pad · group · draw
   ========================================================================== */
(function () {
  'use strict';

  /* Extrusion depth as a FRACTION of the machine, not a fixed number of inches:
     a fixed offset looks like a shadow bug on a 24in bench and disappears on a
     90in jungle gym. */
  function lift(w, d) { return Math.max(1.2, Math.min(4, Math.min(w, d) * 0.055)); }

  function esc(n) { return Math.round(n * 100) / 100; }

  /* ── Extruded primitives ─────────────────────────────────────────────────
     Each returns a side face plus a lit top face. Order matters: sides first so
     tops always overlap them. */

  function box(x, y, w, h, rx, e, tone) {
    if (w <= 0 || h <= 0) return '';
    var t = tone || 'metal';
    return '<rect class="gm3-side" x="' + esc(x + e) + '" y="' + esc(y + e) + '" width="' + esc(w) + '" height="' + esc(h) + '" rx="' + esc(rx) + '"/>' +
           '<rect class="gm3-top gm3-' + t + '" x="' + esc(x) + '" y="' + esc(y) + '" width="' + esc(w) + '" height="' + esc(h) + '" rx="' + esc(rx) + '"/>';
  }

  function disc(cx, cy, r, e, tone) {
    var t = tone || 'metal';
    return '<circle class="gm3-side" cx="' + esc(cx + e) + '" cy="' + esc(cy + e) + '" r="' + esc(r) + '"/>' +
           '<circle class="gm3-top gm3-' + t + '" cx="' + esc(cx) + '" cy="' + esc(cy) + '" r="' + esc(r) + '"/>';
  }

  function shape(pts, e, tone) {
    var t = tone || 'metal';
    var shifted = pts.map(function (p) { return esc(p[0] + e) + ',' + esc(p[1] + e); }).join(' ');
    var flat = pts.map(function (p) { return esc(p[0]) + ',' + esc(p[1]); }).join(' ');
    return '<polygon class="gm3-side" points="' + shifted + '"/>' +
           '<polygon class="gm3-top gm3-' + t + '" points="' + flat + '"/>';
  }

  /* An upholstered pad: red top face, seam down the middle, and a slightly
     proud edge. This is the single most useful cue on the whole plan — it says
     "this is where the body goes", which orients the machine instantly. */
  function pad(x, y, w, h, e, seam) {
    if (w <= 0 || h <= 0) return '';
    var r = Math.min(w, h) * 0.3;
    var out = box(x, y, w, h, r, e, 'pad');
    if (seam !== false) {
      var vertical = h >= w;
      out += vertical
        ? '<line class="gm3-seam" x1="' + esc(x + w / 2) + '" y1="' + esc(y + h * 0.14) + '" x2="' + esc(x + w / 2) + '" y2="' + esc(y + h * 0.86) + '"/>'
        : '<line class="gm3-seam" x1="' + esc(x + w * 0.14) + '" y1="' + esc(y + h / 2) + '" x2="' + esc(x + w * 0.86) + '" y2="' + esc(y + h / 2) + '"/>';
    }
    return out;
  }

  /* A weight stack: plate edges stepped so it reads as a column of steel with
     height, rather than a hatched rectangle. */
  function stack(x, y, w, h, e) {
    var out = [box(x, y, w, h, 1, e, 'stack')];
    var n = Math.max(4, Math.min(14, Math.round(h / 4.5)));
    for (var i = 1; i < n; i++) {
      var yy = y + (h / n) * i;
      out.push('<line class="gm3-plateline" x1="' + esc(x + w * 0.10) + '" y1="' + esc(yy) + '" x2="' + esc(x + w * 0.90) + '" y2="' + esc(yy) + '"/>');
    }
    /* selector pin */
    out.push('<circle class="gm3-pin" cx="' + esc(x + w * 0.5) + '" cy="' + esc(y + h * 0.42) + '" r="' + esc(Math.min(w, h) * 0.07) + '"/>');
    return out.join('');
  }

  /* Plates on a horn: stepped discs, largest outermost, so it reads as loaded. */
  function plates(cx, cy, r, e) {
    return disc(cx, cy, r, e, 'iron') +
           disc(cx, cy, r * 0.68, e * 0.6, 'iron') +
           '<circle class="gm3-pin" cx="' + esc(cx) + '" cy="' + esc(cy) + '" r="' + esc(r * 0.15) + '"/>';
  }

  function flywheel(cx, cy, r, e, spokes) {
    var out = [disc(cx, cy, r, e, 'iron')];
    var n = spokes || 8;
    for (var i = 0; i < n; i++) {
      var a = (Math.PI / n) * i;
      out.push('<line class="gm3-spoke" x1="' + esc(cx - Math.cos(a) * r * 0.86) + '" y1="' + esc(cy - Math.sin(a) * r * 0.86) +
               '" x2="' + esc(cx + Math.cos(a) * r * 0.86) + '" y2="' + esc(cy + Math.sin(a) * r * 0.86) + '"/>');
    }
    out.push('<circle class="gm3-pin" cx="' + esc(cx) + '" cy="' + esc(cy) + '" r="' + esc(r * 0.18) + '"/>');
    return out.join('');
  }

  function bar(x1, y, x2, thick, e) {
    return box(x1, y, x2 - x1, thick, thick / 2, e, 'iron');
  }

  function grip(x, y, len, e, vertical) {
    return vertical
      ? box(x, y, Math.max(1.4, len * 0.13), len, len * 0.06, e, 'grip')
      : box(x, y, len, Math.max(1.4, len * 0.13), len * 0.06, e, 'grip');
  }

  function cable(x1, y1, x2, y2) {
    return '<line class="gm3-cable" x1="' + esc(x1) + '" y1="' + esc(y1) + '" x2="' + esc(x2) + '" y2="' + esc(y2) + '"/>';
  }

  /* ── The station generator ───────────────────────────────────────────────
     Everything selectorized or plate-loaded is one of these. The options are the
     things that actually differ between a leg press and a shoulder press when
     you look down at them:

       load      'stack' | 'plates'   how resistance is applied
       seat      'recline' | 'upright' | 'flat' | 'none'
       arms      'front' | 'over' | 'side' | 'none'   where the handles are
       foot      true    a footplate ahead of the seat (leg machines)
       chest     true    a chest/torso pad (rows, ab machines)
       roller    true    ankle or thigh rollers
  */
  function station(w, d, o) {
    var e = lift(w, d);
    var out = [];
    var loadW = w * 0.26;
    var frameX = o.load === 'stack' ? loadW * 0.95 : w * 0.12;
    var frameW = w - frameX - (o.load === 'stack' ? w * 0.03 : w * 0.12);

    /* base frame — the part bolted to the floor */
    out.push(box(frameX, d * 0.10, frameW, d * 0.84, 1.5, e, 'frame'));
    out.push(box(frameX + frameW * 0.08, d * 0.90, frameW * 0.84, d * 0.08, 1, e * 0.7, 'foot'));

    if (o.load === 'stack') {
      out.push(box(0, d * 0.06, loadW, d * 0.88, 1.5, e, 'frame'));
      out.push(stack(loadW * 0.16, d * 0.14, loadW * 0.68, d * 0.72, e * 0.8));
      out.push(cable(loadW * 0.5, d * 0.14, loadW * 0.5, d * 0.04));
      out.push(cable(loadW * 0.5, d * 0.04, frameX + frameW * 0.5, d * 0.04));
    } else {
      out.push(plates(w * 0.075, d * 0.30, Math.min(w, d) * 0.10, e));
      out.push(plates(w * 0.925, d * 0.30, Math.min(w, d) * 0.10, e));
      out.push(bar(w * 0.06, d * 0.285, w * 0.94, Math.max(1.2, d * 0.022), e * 0.7));
    }

    var cx = frameX + frameW / 2;

    /* seat + back */
    if (o.seat === 'recline') {
      out.push(pad(cx - frameW * 0.20, d * 0.44, frameW * 0.40, d * 0.20, e, false));
      out.push(pad(cx - frameW * 0.23, d * 0.64, frameW * 0.46, d * 0.24, e));
    } else if (o.seat === 'upright') {
      out.push(pad(cx - frameW * 0.19, d * 0.48, frameW * 0.38, d * 0.20, e, false));
      out.push(pad(cx - frameW * 0.22, d * 0.68, frameW * 0.44, d * 0.16, e));
    } else if (o.seat === 'flat') {
      out.push(pad(cx - frameW * 0.16, d * 0.42, frameW * 0.32, d * 0.44, e));
    }

    if (o.chest) out.push(pad(cx - frameW * 0.24, d * 0.32, frameW * 0.48, d * 0.12, e, false));

    /* footplate for leg machines — big, angled, unmistakable */
    if (o.foot) {
      out.push(shape([
        [cx - frameW * 0.30, d * 0.14], [cx + frameW * 0.30, d * 0.14],
        [cx + frameW * 0.24, d * 0.32], [cx - frameW * 0.24, d * 0.32]
      ], e, 'plate'));
      out.push('<line class="gm3-tread" x1="' + esc(cx - frameW * 0.22) + '" y1="' + esc(d * 0.23) + '" x2="' + esc(cx + frameW * 0.22) + '" y2="' + esc(d * 0.23) + '"/>');
    }

    /* handles */
    if (o.arms === 'front') {
      out.push(grip(cx - frameW * 0.40, d * 0.22, Math.min(frameW, d) * 0.26, e, true));
      out.push(grip(cx + frameW * 0.34, d * 0.22, Math.min(frameW, d) * 0.26, e, true));
      out.push(cable(cx - frameW * 0.34, d * 0.24, cx, d * 0.34));
      out.push(cable(cx + frameW * 0.36, d * 0.24, cx, d * 0.34));
    } else if (o.arms === 'over') {
      out.push(bar(cx - frameW * 0.34, d * 0.20, cx + frameW * 0.34, Math.max(1.3, d * 0.024), e * 0.7));
      out.push(cable(cx, d * 0.10, cx, d * 0.20));
      out.push(grip(cx - frameW * 0.34, d * 0.16, Math.min(frameW, d) * 0.14, e * 0.6, true));
      out.push(grip(cx + frameW * 0.30, d * 0.16, Math.min(frameW, d) * 0.14, e * 0.6, true));
    } else if (o.arms === 'side') {
      out.push(grip(frameX - frameW * 0.02, d * 0.40, Math.min(frameW, d) * 0.22, e, true));
      out.push(grip(frameX + frameW * 0.94, d * 0.40, Math.min(frameW, d) * 0.22, e, true));
    }

    if (o.roller) {
      out.push(disc(cx - frameW * 0.16, d * 0.24, Math.min(w, d) * 0.055, e, 'grip'));
      out.push(disc(cx + frameW * 0.16, d * 0.24, Math.min(w, d) * 0.055, e, 'grip'));
    }

    return out.join('');
  }

  /* A tower rig — functional trainers, multi-stations, jungle gyms. `towers` is
     how many weight stacks stand around the frame, which is exactly what the
     product name tells you: 4-stack, 5-stack, 8-stack. */
  function rig(w, d, towers, opts) {
    var e = lift(w, d);
    var o = opts || {};
    var out = [];
    var tw = Math.min(w * 0.22, 26);
    var td = Math.min(d * 0.30, 34);

    /* open frame perimeter */
    out.push('<rect class="gm3-openframe" x="' + esc(w * 0.02) + '" y="' + esc(d * 0.02) + '" width="' + esc(w * 0.96) + '" height="' + esc(d * 0.96) + '" rx="2"/>');

    var spots = [
      [0, 0], [w - tw, 0], [0, d - td], [w - tw, d - td],
      [w / 2 - tw / 2, 0], [0, d / 2 - td / 2], [w - tw, d / 2 - td / 2], [w / 2 - tw / 2, d - td]
    ].slice(0, Math.max(2, Math.min(8, towers)));

    spots.forEach(function (s) {
      out.push(box(s[0], s[1], tw, td, 1.5, e, 'frame'));
      out.push(stack(s[0] + tw * 0.16, s[1] + td * 0.14, tw * 0.68, td * 0.72, e * 0.8));
    });

    /* top crossmembers tie the towers together */
    out.push(box(w * 0.20, d * 0.03, w * 0.60, Math.max(2, d * 0.05), 1, e * 0.7, 'iron'));
    out.push(box(w * 0.20, d * 0.92, w * 0.60, Math.max(2, d * 0.05), 1, e * 0.7, 'iron'));

    /* cable runs across the open middle */
    out.push(cable(tw * 0.5, td, tw * 0.5, d - td));
    out.push(cable(w - tw * 0.5, td, w - tw * 0.5, d - td));

    if (o.bench) out.push(pad(w * 0.42, d * 0.38, w * 0.16, d * 0.26, e));
    if (o.press) {
      out.push(pad(w * 0.40, d * 0.40, w * 0.20, d * 0.14, e, false));
      out.push(grip(w * 0.30, d * 0.30, Math.min(w, d) * 0.16, e, true));
      out.push(grip(w * 0.66, d * 0.30, Math.min(w, d) * 0.16, e, true));
    }
    return out.join('');
  }

  /* ── Photographic art ────────────────────────────────────────────────────
     Real top-down renders beat any vector I can draw, so where art exists it
     wins and the hand-drawn model is the fallback. `window.CTX_GYM_ART` is
     populated by the section from theme assets, keyed by class.

     `meet` not `slice`: the art is letterboxed inside the true footprint rather
     than stretched to fill it. The two are not always the same ratio — the
     treadmill render is 0.33 wide-to-long against a real footprint nearer 0.42 —
     and stretching a photo of a machine to match a number is how a plan starts
     lying. The clearance halo still shows the real footprint either way. */
  /* Art is looked up by EQUIPMENT TYPE first, then by model class.

     Type is the finer grain and it is the grain the artwork is actually being
     produced at — the asset sheet is organised by the 43-value taxonomy, one
     drawing per type. Class is the fallback so a type with no art of its own
     still gets the closest available drawing rather than dropping to vector.

     Only keys VERIFIED to load are honoured (see verifyArt). A missing or
     misnamed asset would otherwise render an empty <image> and blank the machine
     on the plan — a silent failure exactly like the ones this build has already
     produced twice. */
  function typeKey(ty) {
    return String(ty || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  var VERIFIED = null;   /* Set of usable art keys, filled by verifyArt() */

  /* Returns [key, url] so the caller can record what it used and probe only
     that, rather than the whole library. */
  /* Three levels, most specific first:

       HANDLE  one product, pinned. For the exceptions that must not follow their
               type or class — the Zaia All In One keeps the drawing it already
               has even though it classifies as a multi-station.
       TYPE    the 43-value taxonomy. The default grain.
       CLASS   the model class. This is what carries the stack-count rigs:
               classify() already resolves 4/5/8-stack from the product name, so
               a new 4-stack added next month picks up the right drawing with no
               change here at all — which is exactly what Dillon asked for.

     Note `All-In-One Units` is deliberately ABSENT from the type registry. If it
     were present it would sit above class and every stack count would collapse
     back to one shared drawing, which is the bug this replaces. */
  function artEntry(cls, ty, handle, sku) {
    var bySku = window.CTX_GYM_ART_SKU || {};
    var byHandle = window.CTX_GYM_ART_HANDLE || {};
    var byType = window.CTX_GYM_ART_TYPE || {};
    var byClass = window.CTX_GYM_ART || {};
    var tk = typeKey(ty);
    var dead = function (k) { return VERIFIED && VERIFIED[k] === 0; };

    /* SKU outranks everything: it is the only tier that identifies the exact
       machine rather than its family. Map values are [url, length, width] —
       the footprint halves are consumed by the catalog loader, only the url
       matters here. */
    if (sku && bySku[sku] && bySku[sku][0] && !dead('s:' + sku)) return ['s:' + sku, bySku[sku][0]];
    if (handle && byHandle[handle] && !dead('h:' + handle)) return ['h:' + handle, byHandle[handle]];
    if (tk && byType[tk] && !dead('t:' + tk)) return ['t:' + tk, byType[tk]];
    if (cls && byClass[cls] && !dead('c:' + cls)) return ['c:' + cls, byClass[cls]];
    return null;
  }

  function artSrc(cls, ty, handle, sku) {
    var e = artEntry(cls, ty, handle, sku);
    return e ? e[1] : null;
  }

  function artMarkup(cls, ty, w, d, handle, sku) {
    var src = artSrc(cls, ty, handle, sku);
    if (!src) return null;
    /* meet, never slice: the drawing is letterboxed inside the true footprint
       rather than stretched to it. */
    return '<image href="' + src + '" xlink:href="' + src + '" x="0" y="0" ' +
           'width="' + esc(w) + '" height="' + esc(d) + '" ' +
           'preserveAspectRatio="xMidYMid meet" class="gm3-art"/>';
  }

  /* LAZY verification, and it has to be lazy.

     The first version load-tested every declared asset at boot. With 43 drawings
     installed that is ~7MB pulled on every page view, for machines the shopper
     may never see — the plan only ever references the handful of types actually
     placed. Now nothing is probed until a drawing is genuinely used, each key is
     checked at most once, and a failure marks that key dead and asks the caller
     to redraw so the vector model takes over.

     Optimistic by default: an unknown key is treated as good, so the first paint
     shows art rather than flashing a vector and swapping. */
  var probed = {};

  function probeUsed(keys, onFail) {
    var pending = [];

    keys.forEach(function (entry) {
      var key = entry[0], url = entry[1];
      if (probed[key]) return;
      probed[key] = 1;

      pending.push(new Promise(function (res) {
        var img = new Image();
        var done = false;
        var finish = function (good) {
          if (done) return;
          done = true;
          if (!good) { VERIFIED = VERIFIED || {}; VERIFIED[key] = 0; }
          res(good);
        };
        img.onload = function () { finish(img.naturalWidth > 0); };
        img.onerror = function () { finish(false); };
        setTimeout(function () { finish(true); }, 8000);   /* slow != broken */
        img.src = url;
      }));
    });

    if (!pending.length) return;
    Promise.all(pending).then(function (results) {
      if (results.some(function (ok) { return !ok; }) && typeof onFail === 'function') onFail();
    });
  }

  /* Retained for callers that want an explicit no-op; verification is lazy now. */
  function verifyArt() { return Promise.resolve({}); }

  /* Kept for the two models that call it directly. */
  function art(cls, w, d) { return artMarkup(cls, '', w, d); }

  var MODELS = {

    /* ── CARDIO ─────────────────────────────────────────────────────────── */

    treadmill: {
      label: 'Treadmill', group: 'cardio', aspect: 0.42, pad: { x: 14, y: 32 },
      draw: function (w, d) {
        var a = art('treadmill', w, d);
        if (a) return a;
        var e = lift(w, d);
        return [
          box(w * 0.05, d * 0.15, w * 0.90, d * 0.83, 2, e, 'frame'),
          /* belt sits DOWN in the deck, so no extrusion — it is a recess */
          '<rect class="gm3-belt" x="' + esc(w * 0.21) + '" y="' + esc(d * 0.27) + '" width="' + esc(w * 0.58) + '" height="' + esc(d * 0.67) + '" rx="1"/>',
          '<line class="gm3-seam" x1="' + esc(w * 0.5) + '" y1="' + esc(d * 0.30) + '" x2="' + esc(w * 0.5) + '" y2="' + esc(d * 0.91) + '"/>',
          box(w * 0.05, d * 0.22, w * 0.15, d * 0.62, 1.5, e, 'grip'),
          box(w * 0.80, d * 0.22, w * 0.15, d * 0.62, 1.5, e, 'grip'),
          box(w * 0.19, d * 0.20, w * 0.62, d * 0.09, 1.5, e, 'iron'),
          box(w * 0.10, 0, w * 0.80, d * 0.14, 2, e * 1.3, 'console'),
          '<line class="gm3-screen" x1="' + esc(w * 0.26) + '" y1="' + esc(d * 0.07) + '" x2="' + esc(w * 0.74) + '" y2="' + esc(d * 0.07) + '"/>'
        ].join('');
      }
    },

    elliptical: {
      label: 'Elliptical', group: 'cardio', aspect: 0.38, pad: { x: 14, y: 20 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.28, 0, w * 0.44, d * 0.22, 2, e * 1.2, 'console'),
          flywheel(w * 0.50, d * 0.12, Math.min(w, d) * 0.085, e, 8),
          box(w * 0.44, d * 0.22, w * 0.12, d * 0.58, 1, e, 'frame'),
          shape([[w * 0.16, d * 0.34], [w * 0.42, d * 0.34], [w * 0.42, d * 0.95], [w * 0.20, d * 0.95]], e, 'plate'),
          shape([[w * 0.58, d * 0.34], [w * 0.84, d * 0.34], [w * 0.80, d * 0.95], [w * 0.58, d * 0.95]], e, 'plate'),
          grip(w * 0.04, d * 0.28, Math.min(w, d) * 0.30, e, true),
          grip(w * 0.92, d * 0.28, Math.min(w, d) * 0.30, e, true),
          box(w * 0.30, d * 0.95, w * 0.40, d * 0.05, 1, e * 0.7, 'foot')
        ].join('');
      }
    },

    arc: {
      label: 'Arc trainer', group: 'cardio', aspect: 0.45, pad: { x: 14, y: 20 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.22, 0, w * 0.56, d * 0.28, 2, e * 1.2, 'console'),
          flywheel(w * 0.50, d * 0.15, Math.min(w, d) * 0.10, e, 10),
          shape([[w * 0.14, d * 0.32], [w * 0.44, d * 0.32], [w * 0.40, d * 0.97], [w * 0.10, d * 0.97]], e, 'plate'),
          shape([[w * 0.56, d * 0.32], [w * 0.86, d * 0.32], [w * 0.90, d * 0.97], [w * 0.60, d * 0.97]], e, 'plate'),
          grip(w * 0.04, d * 0.30, Math.min(w, d) * 0.26, e, true),
          grip(w * 0.92, d * 0.30, Math.min(w, d) * 0.26, e, true)
        ].join('');
      }
    },

    upright: {
      label: 'Upright bike', group: 'cardio', aspect: 0.50, pad: { x: 12, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.20, 0, w * 0.60, d * 0.10, 1, e * 0.7, 'foot'),
          box(w * 0.20, d * 0.90, w * 0.60, d * 0.10, 1, e * 0.7, 'foot'),
          box(w * 0.40, d * 0.10, w * 0.20, d * 0.78, 1.5, e, 'frame'),
          flywheel(w * 0.50, d * 0.26, Math.min(w, d) * 0.18, e, 8),
          box(w * 0.16, d * 0.12, w * 0.68, d * 0.07, 2, e, 'console'),
          pad(w * 0.34, d * 0.58, w * 0.32, d * 0.22, e),
          disc(w * 0.30, d * 0.44, Math.min(w, d) * 0.055, e * 0.7, 'grip'),
          disc(w * 0.70, d * 0.44, Math.min(w, d) * 0.055, e * 0.7, 'grip')
        ].join('');
      }
    },

    recumbent: {
      label: 'Recumbent bike', group: 'cardio', aspect: 0.36, pad: { x: 12, y: 16 },
      draw: function (w, d) {
        var a = art('recumbent', w, d);
        if (a) return a;
        var e = lift(w, d);
        return [
          box(w * 0.24, 0, w * 0.52, d * 0.08, 1, e * 0.7, 'foot'),
          box(w * 0.42, d * 0.08, w * 0.16, d * 0.80, 1.5, e, 'frame'),
          flywheel(w * 0.50, d * 0.16, Math.min(w, d) * 0.12, e, 8),
          disc(w * 0.28, d * 0.24, Math.min(w, d) * 0.055, e * 0.7, 'grip'),
          disc(w * 0.72, d * 0.24, Math.min(w, d) * 0.055, e * 0.7, 'grip'),
          box(w * 0.20, d * 0.36, w * 0.60, d * 0.06, 2, e, 'console'),
          pad(w * 0.22, d * 0.54, w * 0.56, d * 0.20, e, false),
          pad(w * 0.16, d * 0.74, w * 0.68, d * 0.20, e),
          grip(w * 0.08, d * 0.58, Math.min(w, d) * 0.18, e, true),
          grip(w * 0.88, d * 0.58, Math.min(w, d) * 0.18, e, true)
        ].join('');
      }
    },

    spin: {
      label: 'Indoor cycle', group: 'cardio', aspect: 0.48, pad: { x: 12, y: 14 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.22, 0, w * 0.56, d * 0.08, 1, e * 0.7, 'foot'),
          box(w * 0.22, d * 0.92, w * 0.56, d * 0.08, 1, e * 0.7, 'foot'),
          box(w * 0.42, d * 0.08, w * 0.16, d * 0.82, 1.5, e, 'frame'),
          flywheel(w * 0.50, d * 0.22, Math.min(w, d) * 0.23, e, 14),
          grip(w * 0.20, d * 0.34, Math.min(w, d) * 0.20, e, false),
          pad(w * 0.36, d * 0.58, w * 0.28, d * 0.18, e),
          disc(w * 0.30, d * 0.48, Math.min(w, d) * 0.05, e * 0.7, 'grip'),
          disc(w * 0.70, d * 0.48, Math.min(w, d) * 0.05, e * 0.7, 'grip')
        ].join('');
      }
    },

    airbike: {
      label: 'Air bike', group: 'cardio', aspect: 0.52, pad: { x: 12, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.24, d * 0.90, w * 0.52, d * 0.10, 1, e * 0.7, 'foot'),
          box(w * 0.44, d * 0.24, w * 0.12, d * 0.66, 1.5, e, 'frame'),
          flywheel(w * 0.50, d * 0.28, Math.min(w, d) * 0.29, e, 16),
          pad(w * 0.34, d * 0.62, w * 0.32, d * 0.18, e),
          grip(w * 0.16, d * 0.06, Math.min(w, d) * 0.30, e, true),
          grip(w * 0.80, d * 0.06, Math.min(w, d) * 0.30, e, true)
        ].join('');
      }
    },

    rower: {
      label: 'Rower', group: 'cardio', aspect: 0.25, pad: { x: 12, y: 18 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.18, 0, w * 0.64, d * 0.16, 2, e * 1.2, 'console'),
          flywheel(w * 0.50, d * 0.08, Math.min(w, d) * 0.06, e, 10),
          box(w * 0.42, d * 0.16, w * 0.16, d * 0.82, 1, e, 'frame'),
          '<line class="gm3-rail" x1="' + esc(w * 0.5) + '" y1="' + esc(d * 0.20) + '" x2="' + esc(w * 0.5) + '" y2="' + esc(d * 0.96) + '"/>',
          shape([[w * 0.14, d * 0.16], [w * 0.40, d * 0.16], [w * 0.40, d * 0.30], [w * 0.18, d * 0.30]], e, 'plate'),
          shape([[w * 0.60, d * 0.16], [w * 0.86, d * 0.16], [w * 0.82, d * 0.30], [w * 0.60, d * 0.30]], e, 'plate'),
          pad(w * 0.30, d * 0.54, w * 0.40, d * 0.13, e, false),
          box(w * 0.32, d * 0.96, w * 0.36, d * 0.04, 1, e * 0.6, 'foot')
        ].join('');
      }
    },

    stepper: {
      label: 'Stair climber', group: 'cardio', aspect: 0.57, pad: { x: 14, y: 22 },
      draw: function (w, d) {
        var e = lift(w, d);
        var out = [
          box(w * 0.16, d * 0.10, w * 0.68, d * 0.88, 2, e, 'frame'),
          box(w * 0.14, 0, w * 0.72, d * 0.12, 2, e * 1.3, 'console'),
          box(0, d * 0.16, w * 0.13, d * 0.58, 1.5, e, 'grip'),
          box(w * 0.87, d * 0.16, w * 0.13, d * 0.58, 1.5, e, 'grip')
        ];
        for (var i = 0; i < 7; i++) {
          out.push(box(w * 0.22, d * (0.16 + i * 0.113), w * 0.56, d * 0.078, 0.8, e * 0.55, 'plate'));
        }
        return out.join('');
      }
    },

    stretch: {
      label: 'Stretch trainer', group: 'cardio', aspect: 0.60, pad: { x: 12, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.06, d * 0.28, w * 0.88, d * 0.56, 2, e, 'frame'),
          pad(w * 0.24, d * 0.04, w * 0.52, d * 0.34, e),
          grip(w * 0.10, d * 0.60, Math.min(w, d) * 0.20, e, false),
          grip(w * 0.70, d * 0.60, Math.min(w, d) * 0.20, e, false),
          disc(w * 0.18, d * 0.44, Math.min(w, d) * 0.05, e * 0.7, 'grip'),
          disc(w * 0.82, d * 0.44, Math.min(w, d) * 0.05, e * 0.7, 'grip')
        ].join('');
      }
    },

    /* ── SELECTORIZED, by muscle group ──────────────────────────────────── */

    sel_legs: {
      label: 'Leg machine', group: 'strength', aspect: 0.70, pad: { x: 14, y: 20 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'recline', foot: true, arms: 'side' }); }
    },
    sel_back: {
      label: 'Back machine', group: 'strength', aspect: 0.78, pad: { x: 14, y: 18 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'upright', arms: 'over', chest: true }); }
    },
    sel_chest: {
      label: 'Chest machine', group: 'strength', aspect: 0.80, pad: { x: 14, y: 18 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'upright', arms: 'front' }); }
    },
    sel_shoulders: {
      label: 'Shoulder machine', group: 'strength', aspect: 0.78, pad: { x: 14, y: 18 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'upright', arms: 'over' }); }
    },
    sel_arms: {
      label: 'Arm machine', group: 'strength', aspect: 0.74, pad: { x: 12, y: 16 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'upright', arms: 'front', chest: true }); }
    },
    sel_abs: {
      label: 'Ab machine', group: 'strength', aspect: 0.74, pad: { x: 12, y: 16 },
      draw: function (w, d) { return station(w, d, { load: 'stack', seat: 'upright', arms: 'over', roller: true }); }
    },

    /* ── PLATE-LOADED, by muscle group ──────────────────────────────────── */

    pl_legs: {
      label: 'Leg press', group: 'strength', aspect: 0.62, pad: { x: 16, y: 24 },
      draw: function (w, d) { return station(w, d, { load: 'plates', seat: 'recline', foot: true }); }
    },
    pl_back: {
      label: 'Plate-loaded back', group: 'strength', aspect: 0.80, pad: { x: 16, y: 20 },
      draw: function (w, d) { return station(w, d, { load: 'plates', seat: 'upright', arms: 'over', chest: true }); }
    },
    pl_chest: {
      label: 'Plate-loaded chest', group: 'strength', aspect: 0.82, pad: { x: 16, y: 20 },
      draw: function (w, d) { return station(w, d, { load: 'plates', seat: 'upright', arms: 'front' }); }
    },
    pl_shoulders: {
      label: 'Plate-loaded shoulder', group: 'strength', aspect: 0.80, pad: { x: 16, y: 20 },
      draw: function (w, d) { return station(w, d, { load: 'plates', seat: 'upright', arms: 'over' }); }
    },
    pl_arms: {
      label: 'Plate-loaded arms', group: 'strength', aspect: 0.76, pad: { x: 14, y: 18 },
      draw: function (w, d) { return station(w, d, { load: 'plates', seat: 'upright', arms: 'front', chest: true }); }
    },

    /* ── RIGS ───────────────────────────────────────────────────────────── */

    multi4: {
      label: '4-stack multi', group: 'strength', aspect: 1.00, pad: { x: 20, y: 26 },
      draw: function (w, d) { return rig(w, d, 4, { bench: true }); }
    },
    multi5: {
      label: '5-stack multi', group: 'strength', aspect: 1.00, pad: { x: 20, y: 26 },
      draw: function (w, d) { return rig(w, d, 5, { press: true }); }
    },
    multi8: {
      label: '8-stack jungle gym', group: 'strength', aspect: 1.00, pad: { x: 22, y: 28 },
      draw: function (w, d) { return rig(w, d, 8, { press: true }); }
    },
    functional: {
      label: 'Functional trainer', group: 'strength', aspect: 0.95, pad: { x: 16, y: 22 },
      draw: function (w, d) { return rig(w, d, 2, { bench: true }); }
    },

    smith: {
      label: 'Smith machine', group: 'strength', aspect: 0.90, pad: { x: 16, y: 22 },
      draw: function (w, d) {
        var e = lift(w, d);
        var p = Math.min(w, d) * 0.11;
        return [
          box(w * 0.05, d * 0.90, w * 0.90, d * 0.09, 1, e * 0.7, 'foot'),
          box(0, 0, p, p, 0.5, e * 1.4, 'post'), box(w - p, 0, p, p, 0.5, e * 1.4, 'post'),
          box(0, d * 0.86 - p, p, p, 0.5, e * 1.4, 'post'), box(w - p, d * 0.86 - p, p, p, 0.5, e * 1.4, 'post'),
          '<line class="gm3-guide" x1="' + esc(p / 2) + '" y1="' + esc(p) + '" x2="' + esc(p / 2) + '" y2="' + esc(d * 0.86 - p) + '"/>',
          '<line class="gm3-guide" x1="' + esc(w - p / 2) + '" y1="' + esc(p) + '" x2="' + esc(w - p / 2) + '" y2="' + esc(d * 0.86 - p) + '"/>',
          bar(w * 0.03, d * 0.38, w * 0.97, Math.max(1.6, d * 0.04), e),
          plates(w * 0.09, d * 0.62, Math.min(w, d) * 0.09, e),
          plates(w * 0.91, d * 0.62, Math.min(w, d) * 0.09, e),
          pad(w * 0.40, d * 0.46, w * 0.20, d * 0.38, e)
        ].join('');
      }
    },

    rack: {
      label: 'Power rack', group: 'strength', aspect: 1.00, pad: { x: 18, y: 26 },
      draw: function (w, d) {
        var e = lift(w, d);
        var p = Math.min(w, d) * 0.13;
        return [
          box(0, p * 0.34, w, p * 0.30, 0.5, e * 0.8, 'iron'),
          box(0, d - p * 0.64, w, p * 0.30, 0.5, e * 0.8, 'iron'),
          box(0, 0, p, p, 0.5, e * 1.5, 'post'), box(w - p, 0, p, p, 0.5, e * 1.5, 'post'),
          box(0, d - p, p, p, 0.5, e * 1.5, 'post'), box(w - p, d - p, p, p, 0.5, e * 1.5, 'post'),
          '<line class="gm3-guide" x1="' + esc(p / 2) + '" y1="' + esc(p) + '" x2="' + esc(p / 2) + '" y2="' + esc(d - p) + '"/>',
          '<line class="gm3-guide" x1="' + esc(w - p / 2) + '" y1="' + esc(p) + '" x2="' + esc(w - p / 2) + '" y2="' + esc(d - p) + '"/>',
          bar(w * 0.08, d * 0.34, w * 0.92, Math.max(1.6, d * 0.045), e),
          shape([[p, d * 0.30], [p * 2.2, d * 0.30], [p * 2.2, d * 0.44], [p, d * 0.44]], e * 0.8, 'iron'),
          shape([[w - p, d * 0.30], [w - p * 2.2, d * 0.30], [w - p * 2.2, d * 0.44], [w - p, d * 0.44]], e * 0.8, 'iron')
        ].join('');
      }
    },

    storage: {
      label: 'Storage rack', group: 'storage', aspect: 2.60, pad: { x: 10, y: 14 },
      draw: function (w, d) {
        var e = lift(w, d);
        var out = [box(0, d * 0.16, w, d * 0.68, 1, e, 'frame')];
        var n = Math.max(3, Math.min(12, Math.round(w / 15)));
        for (var i = 0; i < n; i++) {
          out.push(plates(w * ((i + 0.5) / n), d * 0.50, Math.min(d * 0.24, (w / n) * 0.40), e * 0.8));
        }
        return out.join('');
      }
    },

    /* ── BENCHES ────────────────────────────────────────────────────────── */

    benchflat: {
      label: 'Flat bench', group: 'strength', aspect: 0.40, pad: { x: 14, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.12, d * 0.02, w * 0.76, d * 0.08, 1, e * 0.7, 'foot'),
          box(w * 0.12, d * 0.90, w * 0.76, d * 0.08, 1, e * 0.7, 'foot'),
          box(w * 0.44, d * 0.08, w * 0.12, d * 0.84, 1, e * 0.7, 'frame'),
          pad(w * 0.28, d * 0.06, w * 0.44, d * 0.88, e)
        ].join('');
      }
    },

    benchadj: {
      label: 'Adjustable bench', group: 'strength', aspect: 0.42, pad: { x: 14, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.12, d * 0.88, w * 0.76, d * 0.10, 1, e * 0.7, 'foot'),
          box(w * 0.44, d * 0.06, w * 0.12, d * 0.84, 1, e * 0.7, 'frame'),
          pad(w * 0.26, d * 0.04, w * 0.48, d * 0.44, e),
          pad(w * 0.30, d * 0.52, w * 0.40, d * 0.32, e),
          disc(w * 0.50, d * 0.49, Math.min(w, d) * 0.035, e * 0.5, 'grip')
        ].join('');
      }
    },

    benchspec: {
      label: 'Olympic bench', group: 'strength', aspect: 0.72, pad: { x: 16, y: 20 },
      draw: function (w, d) {
        var e = lift(w, d);
        var p = Math.min(w, d) * 0.10;
        return [
          box(w * 0.26, d * 0.92, w * 0.48, d * 0.07, 1, e * 0.7, 'foot'),
          box(0, 0, p, p, 0.5, e * 1.4, 'post'), box(w - p, 0, p, p, 0.5, e * 1.4, 'post'),
          bar(w * 0.03, d * 0.05, w * 0.97, Math.max(1.5, d * 0.038), e),
          plates(w * 0.08, d * 0.068, p * 0.9, e),
          plates(w * 0.92, d * 0.068, p * 0.9, e),
          '<line class="gm3-guide" x1="' + esc(p / 2) + '" y1="' + esc(p) + '" x2="' + esc(p / 2) + '" y2="' + esc(d * 0.30) + '"/>',
          '<line class="gm3-guide" x1="' + esc(w - p / 2) + '" y1="' + esc(p) + '" x2="' + esc(w - p / 2) + '" y2="' + esc(d * 0.30) + '"/>',
          pad(w * 0.36, d * 0.20, w * 0.28, d * 0.74, e)
        ].join('');
      }
    },

    benchab: {
      label: 'Ab / hyper bench', group: 'strength', aspect: 0.50, pad: { x: 12, y: 16 },
      draw: function (w, d) {
        var e = lift(w, d);
        return [
          box(w * 0.18, d * 0.02, w * 0.64, d * 0.10, 1, e * 0.7, 'foot'),
          box(w * 0.28, d * 0.88, w * 0.44, d * 0.09, 1, e * 0.7, 'foot'),
          shape([[w * 0.32, d * 0.16], [w * 0.68, d * 0.16], [w * 0.60, d * 0.86], [w * 0.40, d * 0.86]], e, 'pad'),
          '<line class="gm3-seam" x1="' + esc(w * 0.5) + '" y1="' + esc(d * 0.20) + '" x2="' + esc(w * 0.5) + '" y2="' + esc(d * 0.82) + '"/>',
          disc(w * 0.38, d * 0.22, Math.min(w, d) * 0.055, e, 'grip'),
          disc(w * 0.62, d * 0.22, Math.min(w, d) * 0.055, e, 'grip')
        ].join('');
      }
    },

    /* Placed on the floor since 2026-08-05. Clearance is small but real — you
       need room to lift a dumbbell off a rack, not the walkway a machine wants.
       The drawing here is only a fallback: every free-weight TYPE has its own
       art, so this is what shows if one is ever missing. */
    freeweight: {
      label: 'Free weights', group: 'accessory', aspect: 1.40, pad: { x: 10, y: 14 },
      draw: function (w, d) {
        var e = lift(w, d);
        var out = [box(0, d * 0.18, w, d * 0.64, 1.5, e, 'frame')];
        var n = Math.max(2, Math.min(8, Math.round(w / 12)));
        for (var i = 0; i < n; i++) {
          out.push(plates(w * ((i + 0.5) / n), d * 0.5, Math.min(d * 0.26, (w / n) * 0.38), e * 0.8));
        }
        return out.join('');
      }
    }
  };

  /* Legacy aliases so a saved link or older classification still resolves. */
  MODELS.selector = MODELS.sel_chest;
  MODELS.plate = MODELS.pl_chest;
  MODELS.allinone = MODELS.multi5;

  /* ==========================================================================
     ROOM FEATURES — doors, and the things you have to design around
     ==========================================================================
     Not equipment. These are the parts of the room itself: where the door is,
     what its open path sweeps, where the column stands. They matter because a
     plan that ignores them is a plan that puts a leg press in front of the only
     door.

     Each feature draws in its own local box (0..w by 0..d, inches) and reports a
     KEEPOUT box — the floor a machine may not occupy. For a swing door the
     keepout is much larger than the door itself: it is the quarter-circle the
     leaf sweeps, which is exactly the thing people forget.
     ========================================================================== */
  var WALL = 5;   /* nominal wall thickness the openings sit in, inches */

  /* ---------------------------------------------------------------------------
     Real scaled models, 20 px/inch, from Dillon's ancillary asset pack. The door
     art already CONTAINS its travel path, so the keepout is simply the asset's
     own footprint — a 36in door with a 36in swing is a 36x36 asset, and nothing
     extra has to be computed.

     l is length, w is width, matching the manifest. The art is drawn with
     LENGTH running vertically, so on the plan w is across and l is front-to-back
     — the same convention the equipment art uses.
     --------------------------------------------------------------------------- */
  var FEATURES = {
    "single-door-left-inswing"                : { label: "Single Door Left Inswing",           cat: "arch", l: 36, w: 36 },
    "single-door-right-inswing"               : { label: "Single Door Right Inswing",          cat: "arch", l: 36, w: 36 },
    "single-door-left-outswing"               : { label: "Single Door Left Outswing",          cat: "arch", l: 36, w: 36 },
    "single-door-right-outswing"              : { label: "Single Door Right Outswing",         cat: "arch", l: 36, w: 36 },
    "double-door-inswing"                     : { label: "Double Door Inswing",                cat: "arch", l: 72, w: 36 },
    "sectional-garage-door-with-tracks"       : { label: "Sectional Garage Door With Tracks",  cat: "arch", l: 108, w: 96 },
    "warehouse-coiling-rollup-door"           : { label: "Warehouse Coiling Rollup Door",      cat: "arch", l: 96, w: 18 },
    "straight-desk-with-chair"                : { label: "Straight Desk With Chair",           cat: "fix", l: 60, w: 54 },
    "l-shaped-desk-with-chair"                : { label: "L Shaped Desk With Chair",           cat: "fix", l: 72, w: 72 },
    "reception-counter-with-chair"            : { label: "Reception Counter With Chair",       cat: "fix", l: 72, w: 48 },
    "wall-drinking-fountain"                  : { label: "Wall Drinking Fountain",             cat: "fix", l: 20, w: 18 },
    "bottle-filler-drinking-station"          : { label: "Bottle Filler Drinking Station",     cat: "fix", l: 24, w: 24 },
    "locker-bank"                             : { label: "Locker Bank",                        cat: "fix", l: 72, w: 18 },
    "storage-shelving"                        : { label: "Storage Shelving",                   cat: "fix", l: 72, w: 24 },
    "towel-cubby-station"                     : { label: "Towel Cubby Station",                cat: "fix", l: 48, w: 24 },
    "vending-machine"                         : { label: "Vending Machine",                    cat: "fix", l: 36, w: 34 },
    "commercial-refrigerator"                 : { label: "Commercial Refrigerator",            cat: "fix", l: 36, w: 36 },
    "waste-recycling-station"                 : { label: "Waste Recycling Station",            cat: "fix", l: 36, w: 24 },
    "gym-floor-scale"                         : { label: "Gym Floor Scale",                    cat: "fix", l: 24, w: 24 },
    "tripod-turnstile"                        : { label: "Tripod Turnstile",                   cat: "fix", l: 48, w: 36 },
    "toilet"                                  : { label: "Toilet",                             cat: "fix", l: 48, w: 30 },
    "wall-mounted-sink"                       : { label: "Wall Mounted Sink",                  cat: "fix", l: 24, w: 22 },
    "shower-stall"                            : { label: "Shower Stall",                       cat: "fix", l: 36, w: 36 },
    "mop-service-sink"                        : { label: "Mop Service Sink",                   cat: "fix", l: 36, w: 36 },
    "waiting-bench"                           : { label: "Waiting Bench",                      cat: "fix", l: 72, w: 24 },
    "round-table-four-chairs"                 : { label: "Round Table Four Chairs",            cat: "fix", l: 72, w: 72 },
    "cleaning-cart"                           : { label: "Cleaning Cart",                      cat: "fix", l: 48, w: 24 },
    "towel-laundry-cart"                      : { label: "Towel Laundry Cart",                 cat: "fix", l: 48, w: 30 },
    "water-cooler"                            : { label: "Water Cooler",                       cat: "fix", l: 18, w: 18 }
  };

  Object.keys(FEATURES).forEach(function (k) {
    var f = FEATURES[k];
    f.w = f.w; f.d = f.l;
    f.art = (window.CTX_FEAT_ART || {})[k] || '';
    f.path = (window.CTX_FEAT_PATH || {})[k] || '';
    f.draw = function (w, d) {
      if (!f.art) return '';
      return '<image href="' + f.art + '" xlink:href="' + f.art + '" x="0" y="0" width="' +
             esc(w) + '" height="' + esc(d) + '" preserveAspectRatio="none" class="gf-art"/>';
    };
    /* The whole asset footprint is the keepout: for a door that already includes
       the swept path, and for a fixture it is the object itself. */
    f.keepout = function (w, d) { return { w: w, d: d }; };
  });
  window.CTXGymModels = MODELS;
  window.CTXGymFeatures = FEATURES;
  window.CTXGymArt = {
    markup: artMarkup, verify: verifyArt, typeKey: typeKey,
    entry: artEntry, probeUsed: probeUsed
  };
})();
